import { useMemo, useEffect, useState } from 'react'
import { useAppSelector } from '@/redux/hooks'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { useGamePhase } from '@/hooks/useGamePhase'
import { ActiveTab, isAIPlayerTab } from '@/constants/chat'
import { NegotiationType } from '@/redux/types/negotiationWebsocket'
import { getTeamCommunicatorForRound } from '@/api/team'
import { TeamCommunicator } from '@/redux/types/team'
import { TimerMode } from '@/components/settings/timer-configuration/types'

export const useTeamCommunicator = () => {
  const { user } = useEffectiveUser()
  const { roundId, isDecisionPhase } = useGamePhase()
  const activeTab = useAppSelector((state) => state.chat.activeTab)
  const negotiations = useAppSelector(
    (state) => state.negotiationWS.negotiations
  )
  const roomMembers = useAppSelector((state) => state.room.roomMembers)
  const currentRoom = useAppSelector((state) => state.room.currentRoom)

  const [teamCommunicator, setTeamCommunicator] =
    useState<TeamCommunicator | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const isNegotiationTab = useMemo(
    () =>
      activeTab !== ActiveTab.Advisor &&
      activeTab !== ActiveTab.Adjudication &&
      activeTab !== ActiveTab.TeamRoom &&
      activeTab !== ActiveTab.Decision &&
      !isAIPlayerTab(activeTab) &&
      activeTab !== ActiveTab.Summary,
    [activeTab]
  )

  const isTeamRoomTab = useMemo(
    () => activeTab === ActiveTab.TeamRoom,
    [activeTab]
  )

  const currentNegotiation = useMemo(() => {
    if (isNegotiationTab) {
      return negotiations.find(
        (negotiation) => negotiation.negotiationId === activeTab
      )
    }
    if (isTeamRoomTab) {
      return negotiations.find(
        (negotiation) =>
          negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM
      )
    }
    return null
  }, [isNegotiationTab, isTeamRoomTab, negotiations, activeTab])

  const isPublicNegotiation = useMemo(
    () => currentNegotiation?.negotiation_type === NegotiationType.PUBLIC_ROOM,
    [currentNegotiation]
  )

  const userTeam = useMemo(() => {
    if (!user?.email) return null
    const member = roomMembers.find(
      (member) => member.member_email === user.email
    )
    return member?.teamId || null
  }, [user?.email, roomMembers])

  const isTeamMode = useMemo(
    () => currentRoom?.game_mode === TimerMode.TEAM_MODE,
    [currentRoom?.game_mode]
  )

  const shouldFetchCommunicator = useMemo(() => {
    const negotiationContext = isNegotiationTab && isPublicNegotiation && isTeamMode
    const decisionContext =
      (activeTab === ActiveTab.Decision || isTeamRoomTab) &&
      isDecisionPhase &&
      isTeamMode
    return (
      (negotiationContext || decisionContext) && userTeam && roundId && user?.id
    )
  }, [
    activeTab,
    isNegotiationTab,
    isPublicNegotiation,
    isTeamRoomTab,
    isDecisionPhase,
    isTeamMode,
    userTeam,
    roundId,
    user?.id,
  ])

  useEffect(() => {
    const fetchTeamCommunicator = async () => {
      if (!shouldFetchCommunicator) {
        setTeamCommunicator(null)
        return
      }

      setIsLoading(true)
      try {
        let communicator = await getTeamCommunicatorForRound(
          userTeam!,
          roundId!
        )
        
        // If no communicator found for current round, fallback to previous round
        if (!communicator && roundId! > 1) {
          try {
            const previousRoundId = roundId! - 1
            communicator = await getTeamCommunicatorForRound(
              userTeam!,
              previousRoundId
            )
          } catch (fallbackError) {
            console.error(
              'Failed to fetch previous round communicator as fallback:',
              fallbackError
            )
          }
        }

        setTeamCommunicator(communicator)
      } catch (error) {
        console.error('Failed to fetch team communicator:', error)
        setTeamCommunicator(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTeamCommunicator()
  }, [shouldFetchCommunicator, userTeam, roundId, user?.id])

  const isTeamCommunicator = useMemo(() => {
    if (isNegotiationTab && !isPublicNegotiation) {
      return true
    }

    if (isTeamRoomTab && !(isDecisionPhase && isTeamMode)) {
      return true
    }

    if (!isTeamMode && !isPublicNegotiation) {
      return true
    }

    if (
      (isNegotiationTab && isPublicNegotiation) ||
      (isDecisionPhase && isTeamMode)
    ) {
      if (!teamCommunicator || !user?.email) {
        return false
      }
      return teamCommunicator.communicator_email === user.email
    }

    return true
  }, [
    isNegotiationTab,
    isPublicNegotiation,
    isTeamRoomTab,
    isDecisionPhase,
    isTeamMode,
    teamCommunicator,
    user?.email,
  ])

  const shouldShowChatPill = useMemo(() => {
    if (isTeamRoomTab) {
      return true
    }

    if (!isNegotiationTab) {
      return true
    }

    if (!isPublicNegotiation) {
      return true
    }

    if (isPublicNegotiation) {
      if (!isTeamMode) {
        return true
      }

      if (!userTeam) {
        return false
      }

      if (isLoading) {
        return true
      }

      return isTeamCommunicator
    }

    return true
  }, [
    isNegotiationTab,
    isTeamRoomTab,
    isPublicNegotiation,
    isTeamMode,
    isTeamCommunicator,
    isLoading,
    userTeam,
  ])

  return {
    isTeamCommunicator,
    isLoading,
    teamCommunicator,
    shouldShowChatPill,
    isPublicNegotiation,
    isTeamMode,
    userTeam,
  }
}
